import { useEffect, useMemo, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import {
  Activity,
  BadgeCheck,
  CreditCard,
  Database,
  ExternalLink,
  LayoutDashboard,
  LogOut,
  Mail,
  Megaphone,
  Monitor,
  PlugZap,
  RefreshCw,
  UserPlus,
  UserRound,
  Users,
} from "lucide-react";
import "./App.css";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

type TeamRole = "admin" | "manager" | "team";
type Section = "dashboard" | "team" | "customers" | "plans" | "billing" | "runs" | "marketing";

type TeamUser = {
  id: number;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: TeamRole;
  is_active: boolean;
  created_at: string;
  updated_at?: string | null;
};

type AppUser = {
  id: number;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  plan_slug: string | null;
  auth_provider: string | null;
  auth_providers: string[] | null;
  is_active: boolean;
  created_at: string;
  updated_at?: string | null;
};

type Profile = {
  user_id: string;
  full_name: string | null;
  email: string | null;
  access_status: "active" | "inactive" | null;
};

type Plan = {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
  capabilities?: Record<string, unknown> | null;
};

type SubmissionRun = {
  submission_id: string;
  created_at: string;
  status: string | null;
  error_class: string | null;
  pine_user_id: string | null;
  pine_plan_id: string | null;
};

type SecurityEvent = {
  user_id: string | null;
  email: string | null;
  event_type: "login_success" | "logout" | "login_failed" | string;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

type ActivityItem = {
  time: string;
  event: string;
  actor: string;
  detail: string;
  actorUserId?: string | null;
  targetUserId?: string | null;
};

type DodoSubscriptionState = {
  user_id: string;
  dodo_customer_id: string | null;
  dodo_subscription_id: string | null;
  failed_attempts: number | null;
  last_event_id: string | null;
  updated_at: string | null;
};

type DodoWebhookEvent = {
  event_id: string;
  event_type: string | null;
  user_id?: string | null;
  payload?: Record<string, unknown> | null;
  created_at: string;
};

type SelectedUser =
  | { kind: "team"; user: TeamUser; profile: Profile | null }
  | { kind: "customer"; user: AppUser };

const supabase = getSupabaseClient();
const ADMIN_IDLE_MS = 30 * 60 * 1000;
const ADMIN_MAX_MS = 12 * 60 * 60 * 1000;
const ADMIN_ACTIVITY_THROTTLE_MS = 10 * 1000;
const ADMIN_CHECK_MS = 15 * 1000;
const ADMIN_SESSION_START_KEY = "pe_admin_session_started_at";
const ADMIN_SESSION_LAST_ACTIVITY_KEY = "pe_admin_last_activity_at";

function readTimestamp(key: string): number {
  const raw = window.localStorage.getItem(key);
  if (!raw) return 0;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function writeTimestamp(key: string, value: number) {
  window.localStorage.setItem(key, String(value));
}

function clearAdminSessionWindow() {
  window.localStorage.removeItem(ADMIN_SESSION_START_KEY);
  window.localStorage.removeItem(ADMIN_SESSION_LAST_ACTIVITY_KEY);
}

function ensureAdminSessionWindow() {
  const now = Date.now();
  if (!readTimestamp(ADMIN_SESSION_START_KEY)) writeTimestamp(ADMIN_SESSION_START_KEY, now);
  if (!readTimestamp(ADMIN_SESSION_LAST_ACTIVITY_KEY)) writeTimestamp(ADMIN_SESSION_LAST_ACTIVITY_KEY, now);
}

function getAdminTimeoutType(now: number): "idle" | "max" | null {
  const sessionStart = readTimestamp(ADMIN_SESSION_START_KEY);
  const lastActivity = readTimestamp(ADMIN_SESSION_LAST_ACTIVITY_KEY);
  if (!sessionStart || !lastActivity) return null;
  if (now - sessionStart >= ADMIN_MAX_MS) return "max";
  if (now - lastActivity >= ADMIN_IDLE_MS) return "idle";
  return null;
}

function normalizeFailureCode(message?: string | null): string {
  const text = (message ?? "").toLowerCase();
  if (text.includes("invalid login credentials")) return "invalid_credentials";
  if (text.includes("email not confirmed")) return "email_not_confirmed";
  if (text.includes("rate limit")) return "rate_limited";
  return "unknown_auth_error";
}

function getDisplayName(user: { first_name?: string; last_name?: string }, profile?: Profile | null) {
  if (profile?.full_name?.trim()) return profile.full_name.trim();
  const combined = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
  return combined || "Unnamed user";
}

function formatAuthProvider(provider?: string | null): string {
  const value = String(provider ?? "").trim().toLowerCase();
  if (!value) return "Email";
  if (value === "github") return "GitHub";
  if (value === "email") return "Email";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatLocalDateTime(value?: string | null) {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString();
}

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [checkingAdmin, setCheckingAdmin] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState<string | null>(null);

  const [isAdmin, setIsAdmin] = useState(false);
  const [teamUsers, setTeamUsers] = useState<TeamUser[]>([]);
  const [customerUsers, setCustomerUsers] = useState<AppUser[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [runs, setRuns] = useState<SubmissionRun[]>([]);
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
  const [dodoSubscriptionStates, setDodoSubscriptionStates] = useState<DodoSubscriptionState[]>([]);
  const [dodoWebhookEvents, setDodoWebhookEvents] = useState<DodoWebhookEvent[]>([]);
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [section, setSection] = useState<Section>("dashboard");
  const [query, setQuery] = useState("");
  const [loadingData, setLoadingData] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [lastDbEcho, setLastDbEcho] = useState<string | null>(null);

  const [newPlanCode, setNewPlanCode] = useState("");
  const [newPlanName, setNewPlanName] = useState("");
  const [planCapabilityDrafts, setPlanCapabilityDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    const boot = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      setLoadingSession(false);
    };

    boot();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setSaveMessage(null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const baseVisibleUsers = section === "customers" ? customerUsers : teamUsers;

  const visibleUsers = useMemo(() => {
    if (!query.trim()) return baseVisibleUsers;
    const q = query.trim().toLowerCase();
    return baseVisibleUsers.filter((user) => {
      const profile = profiles[user.user_id];
      const name = section === "customers" ? getDisplayName(user) : getDisplayName(user, profile);
      return (
        name.toLowerCase().includes(q) ||
        user.email.toLowerCase().includes(q) ||
        user.user_id.toLowerCase().includes(q)
      );
    });
  }, [baseVisibleUsers, profiles, query, section]);

  const selected = useMemo<SelectedUser | null>(() => {
    if (!selectedUserId) return null;

    if (section === "customers") {
      const user = customerUsers.find((u) => u.id === selectedUserId);
      if (!user) return null;
      return { kind: "customer", user };
    }

    const user = teamUsers.find((u) => u.id === selectedUserId);
    if (!user) return null;
    return { kind: "team", user, profile: profiles[user.user_id] ?? null };
  }, [selectedUserId, section, customerUsers, teamUsers, profiles]);

  const loadAdminData = async () => {
    if (!session?.user?.id) return;
    setLoadingData(true);
    setPageError(null);

    const { data: me, error: meError } = await supabase
      .from("admin_users")
      .select("id,user_id,role,is_active")
      .eq("user_id", session.user.id)
      .maybeSingle();

    if (meError || !me || me.role !== "admin" || !me.is_active) {
      setIsAdmin(false);
      setTeamUsers([]);
      setCustomerUsers([]);
      setLoadingData(false);
      setPageError("Account is not admin in public.admin_users.");
      return;
    }

    setIsAdmin(true);

    const [teamRes, customerRes, plansRes, submissionsRes, securityRes, dodoStateRes, dodoEventsRes] = await Promise.all([
      supabase
        .from("admin_users")
        .select("id,user_id,first_name,last_name,email,role,is_active,created_at,updated_at")
        .in("role", ["admin", "manager", "team"])
        .order("id", { ascending: true }),
      supabase
        .from("app_users")
        .select("id,user_id,first_name,last_name,email,plan_slug,auth_provider,auth_providers,is_active,created_at,updated_at")
        .order("id", { ascending: true }),
      supabase.from("plans").select("id,code,name,is_active,capabilities").order("name", { ascending: true }),
      supabase
        .from("submissions")
        .select("submission_id,created_at,status,error_class,pine_user_id,pine_plan_id")
        .order("created_at", { ascending: false })
        .limit(300),
      supabase
        .from("security_events")
        .select("user_id,email,event_type,metadata,created_at")
        .order("created_at", { ascending: false })
        .limit(300),
      supabase
        .from("dodo_subscription_state")
        .select("user_id,dodo_customer_id,dodo_subscription_id,failed_attempts,last_event_id,updated_at"),
      supabase
        .from("dodo_webhook_events")
        .select("event_id,event_type,user_id,payload,created_at")
        .order("created_at", { ascending: false })
        .limit(500),
    ]);

    if (teamRes.error) {
      setLoadingData(false);
      setPageError(teamRes.error.message);
      return;
    }
    if (customerRes.error) {
      setLoadingData(false);
      setPageError(customerRes.error.message);
      return;
    }
    const safeTeamUsers = (teamRes.data ?? []) as TeamUser[];
    const teamUserIds = new Set(safeTeamUsers.map((u) => u.user_id));
    const safeCustomerUsers = ((customerRes.data ?? []) as AppUser[]).filter((u) => !teamUserIds.has(u.user_id));
    setTeamUsers(safeTeamUsers);
    setCustomerUsers(safeCustomerUsers);
    const loadedPlans = (plansRes.data ?? []) as Plan[];
    setPlans(loadedPlans);
    setPlanCapabilityDrafts(
      Object.fromEntries(
        loadedPlans.map((p) => [
          p.id,
          JSON.stringify((p.capabilities ?? {}) as Record<string, unknown>, null, 2),
        ]),
      ),
    );
    const pageErrors: string[] = [];
    if (submissionsRes.error) {
      setRuns([]);
      pageErrors.push(`Submissions query failed: ${submissionsRes.error.message}`);
    } else {
      setRuns((submissionsRes.data ?? []) as SubmissionRun[]);
    }
    if (securityRes.error) {
      setSecurityEvents([]);
    } else {
      setSecurityEvents((securityRes.data ?? []) as SecurityEvent[]);
    }
    if (dodoStateRes.error) {
      setDodoSubscriptionStates([]);
      pageErrors.push(`Billing state query failed: ${dodoStateRes.error.message}`);
    } else {
      setDodoSubscriptionStates((dodoStateRes.data ?? []) as DodoSubscriptionState[]);
    }
    if (dodoEventsRes.error) {
      setDodoWebhookEvents([]);
      pageErrors.push(`Billing events query failed: ${dodoEventsRes.error.message}`);
    } else {
      setDodoWebhookEvents((dodoEventsRes.data ?? []) as DodoWebhookEvent[]);
    }
    if (pageErrors.length > 0) {
      setPageError(pageErrors.join(" | "));
    }

    const nextVisible = section === "customers" ? safeCustomerUsers : safeTeamUsers;
    if (!selectedUserId && nextVisible.length > 0) setSelectedUserId(nextVisible[0].id);

    const userIds = Array.from(new Set(safeTeamUsers.map((u) => u.user_id)));
    if (userIds.length > 0) {
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("user_id,full_name,email,access_status")
        .in("user_id", userIds);
      if (!profilesError) {
        setProfiles(Object.fromEntries((profilesData ?? []).map((p) => [p.user_id, p as Profile])));
      }
    }

    setLoadingData(false);
  };

  useEffect(() => {
    if (session) {
      setCheckingAdmin(true);
      loadAdminData().finally(() => setCheckingAdmin(false));
    } else {
      setIsAdmin(false);
      setTeamUsers([]);
      setCustomerUsers([]);
      setSecurityEvents([]);
      setDodoSubscriptionStates([]);
      setDodoWebhookEvents([]);
      setSelectedUserId(null);
      setCheckingAdmin(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  useEffect(() => {
    if (visibleUsers.length === 0) {
      setSelectedUserId(null);
      return;
    }
    if (!visibleUsers.some((u) => u.id === selectedUserId)) {
      setSelectedUserId(visibleUsers[0].id);
    }
  }, [section, selectedUserId, visibleUsers]);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoginError(null);
    const { error } = await supabase.auth.signInWithPassword({ email: loginEmail, password: loginPassword });
    if (error) {
      setLoginError(error.message);
      await supabase.functions.invoke("log-security-event", {
        body: {
          event_type: "login_failed",
          email: loginEmail.trim().toLowerCase(),
          metadata: { failure_code: normalizeFailureCode(error.message) },
        },
      });
      return;
    }
    await supabase.functions.invoke("log-security-event", {
      body: {
        event_type: "login_success",
        email: loginEmail.trim().toLowerCase(),
        metadata: {},
      },
    });
  };

  const handleLogout = async (metadata: Record<string, unknown> = {}) => {
    try {
      await supabase.functions.invoke("log-security-event", {
        body: {
          event_type: "logout",
          email: session?.user?.email?.trim().toLowerCase() ?? null,
          metadata,
        },
      });
    } catch (_error) {
      // keep logout resilient even if event logging fails
    }
    clearAdminSessionWindow();
    await supabase.auth.signOut();
  };

  useEffect(() => {
    if (!session?.user?.id) {
      clearAdminSessionWindow();
      return;
    }

    let activityWriteAt = 0;
    let logoutInFlight = false;

    const touchActivity = (force = false) => {
      const now = Date.now();
      if (!force && now - activityWriteAt < ADMIN_ACTIVITY_THROTTLE_MS) return;
      activityWriteAt = now;
      writeTimestamp(ADMIN_SESSION_LAST_ACTIVITY_KEY, now);
    };

    const runTimeoutCheck = (source: string) => {
      if (logoutInFlight) return;
      const timeoutType = getAdminTimeoutType(Date.now());
      if (!timeoutType) return;
      logoutInFlight = true;
      clearAdminSessionWindow();
      void handleLogout({
        reason: "auto_timeout",
        timeout_type: timeoutType,
        timeout_source: source,
      });
    };

    const onActivity = () => {
      touchActivity(false);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      runTimeoutCheck("visibility");
      if (!logoutInFlight) touchActivity(true);
    };

    ensureAdminSessionWindow();
    touchActivity(true);
    document.addEventListener("visibilitychange", onVisibilityChange, true);
    window.addEventListener("pointerdown", onActivity, true);
    window.addEventListener("keydown", onActivity, true);
    window.addEventListener("scroll", onActivity, true);
    window.addEventListener("touchstart", onActivity, true);
    const intervalId = window.setInterval(() => runTimeoutCheck("interval"), ADMIN_CHECK_MS);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange, true);
      window.removeEventListener("pointerdown", onActivity, true);
      window.removeEventListener("keydown", onActivity, true);
      window.removeEventListener("scroll", onActivity, true);
      window.removeEventListener("touchstart", onActivity, true);
    };
  }, [session?.user?.id]);

  const saveUser = async (form: {
    firstName: string;
    lastName: string;
    email: string;
    accessStatus: "active" | "inactive";
    role?: TeamRole;
    planSlug: string;
  }) => {
    if (!selected) return;
    setSaveMessage(null);
    setLastDbEcho(null);

    const isActive = form.accessStatus === "active";
    const userId = selected.user.user_id;

    if (selected.kind === "team") {
      const { error: teamError } = await supabase
        .from("admin_users")
        .update({
          first_name: form.firstName,
          last_name: form.lastName,
          email: form.email,
          role: form.role ?? selected.user.role,
          is_active: isActive,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selected.user.id);

      if (teamError) {
        setSaveMessage(`Save failed: ${teamError.message}`);
        setLastDbEcho(teamError.message);
        return;
      }
      setLastDbEcho("admin_users update OK");
    } else {
      const normalizedPlanSlug = form.planSlug.trim().toLowerCase();
      const nextPlanSlug = normalizedPlanSlug || null;
      const payload = {
        user_id: userId,
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        is_active: isActive,
        plan_slug: nextPlanSlug,
        updated_at: new Date().toISOString(),
      };

      const { data: customerData, error: customerError } = await supabase
        .from("app_users")
        .update(payload)
        .eq("id", selected.user.id)
        .select("id,user_id,first_name,last_name,email,plan_slug,auth_provider,auth_providers,is_active,updated_at");

      if (customerError) {
        setSaveMessage(`Save failed: ${customerError.message}`);
        setLastDbEcho(customerError.message);
        return;
      }

      if (!customerData || customerData.length === 0) {
        const { data: insertData, error: insertError } = await supabase
          .from("app_users")
          .insert(payload)
          .select("id,user_id,first_name,last_name,email,plan_slug,auth_provider,auth_providers,is_active,updated_at");
        if (insertError) {
          setSaveMessage(`Save failed: ${insertError.message}`);
          setLastDbEcho(insertError.message);
          return;
        }
        setLastDbEcho(JSON.stringify(insertData?.[0] ?? null));
      }

      const { data: verifyRow, error: verifyError } = await supabase
        .from("app_users")
        .select("user_id,first_name,last_name,email,plan_slug,auth_provider,auth_providers,is_active,updated_at")
        .eq("user_id", userId)
        .maybeSingle();

      if (verifyError) {
        setSaveMessage(`Save verify failed: ${verifyError.message}`);
        setLastDbEcho(verifyError.message);
        return;
      }

      if (
        !verifyRow ||
        verifyRow.first_name !== form.firstName ||
        verifyRow.last_name !== form.lastName ||
        verifyRow.email !== form.email ||
        Boolean(verifyRow.is_active) !== isActive ||
        (verifyRow.plan_slug ?? null) !== nextPlanSlug
      ) {
        setSaveMessage("Save verify failed: app_users did not persist expected values.");
        setLastDbEcho(JSON.stringify(verifyRow));
        return;
      }

      if (customerData && customerData.length > 0) setLastDbEcho(JSON.stringify(customerData[0]));
      else setLastDbEcho(JSON.stringify(verifyRow));
    }

    setSaveMessage("Saved.");
    await loadAdminData();
  };

  const createPlan = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newPlanCode.trim() || !newPlanName.trim()) return;

    const { error } = await supabase.from("plans").insert({
      code: newPlanCode.trim().toLowerCase(),
      name: newPlanName.trim(),
      is_active: true,
      capabilities: {},
    });

    if (error) {
      setSaveMessage(`Plan create failed: ${error.message}`);
      return;
    }

    setNewPlanCode("");
    setNewPlanName("");
    setSaveMessage("Plan created.");
    await loadAdminData();
  };

  // DEV NOTES
  // Date: 2026-05-20
  // Reason: centralize plan gating without breaking existing end-to-end flow
  // What changed: document-first capability gating migration, resolver-backed enforcement, frontend capability rendering
  // System Tracker section name: Plan Gating Migration
  const savePlanCapabilities = async (planId: string) => {
    const raw = planCapabilityDrafts[planId] ?? "{}";
    let parsed: Record<string, unknown> = {};
    try {
      parsed = JSON.parse(raw);
    } catch (_err) {
      setSaveMessage("Plan capabilities must be valid JSON.");
      return;
    }

    const { error } = await supabase.from("plans").update({ capabilities: parsed }).eq("id", planId);
    if (error) {
      setSaveMessage(`Plan capability save failed: ${error.message}`);
      return;
    }

    setSaveMessage("Plan capabilities saved.");
    await loadAdminData();
  };

  // DEV NOTES
  // Date: 2026-05-20
  // Reason: centralize plan gating without breaking existing end-to-end flow
  // What changed: document-first capability gating migration, resolver-backed enforcement, frontend capability rendering
  // System Tracker section name: Plan Gating Migration
  const savePlanIsActive = async (planId: string, isActive: boolean) => {
    const { error } = await supabase.from("plans").update({ is_active: isActive }).eq("id", planId);
    if (error) {
      setSaveMessage(`Plan status save failed: ${error.message}`);
      return;
    }
    setSaveMessage(`Plan ${isActive ? "activated" : "deactivated"}.`);
    await loadAdminData();
  };

  const savePlanBetaAccess = async (planId: string, enabled: boolean) => {
    const plan = plans.find((p) => p.id === planId);
    const base = plan?.capabilities && typeof plan.capabilities === "object" ? plan.capabilities : {};
    const nextCapabilities = {
      ...(base as Record<string, unknown>),
      access_enabled: enabled,
    };

    const { error } = await supabase.from("plans").update({ capabilities: nextCapabilities }).eq("id", planId);
    if (error) {
      setSaveMessage(`Beta access save failed: ${error.message}`);
      return;
    }
    setSaveMessage(`Access ${enabled ? "enabled" : "disabled"} for plan.`);
    await loadAdminData();
  };

  if (loadingSession || (session && checkingAdmin)) return <div className="center-shell">Loading...</div>;

  if (!session) {
    return (
      <div className="center-shell">
        <form className="panel login-panel" onSubmit={handleLogin}>
          <img
            src="https://pineengine.com/wp-content/uploads/2026/05/Pine_Engine_AI_Logo-Clear.png"
            alt="Pine Engine Admin"
            className="v1-brand-logo login-brand-logo"
          />
          <p>Admin login</p>
          <label>
            Email
            <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required />
          </label>
          <label>
            Password
            <input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required />
          </label>
          <button type="submit">Sign in</button>
          {loginError && <div className="error">{loginError}</div>}
        </form>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="center-shell">
        <div className="panel">
          <h1>Pine Engine Admin</h1>
          <p>Signed in as {session.user.email}</p>
          <p className="error">{pageError ?? "Admin access required."}</p>
          <button onClick={handleLogout}>Sign out</button>
        </div>
      </div>
    );
  }

  const titleBySection: Record<Section, string> = {
    dashboard: "Overview",
    team: "Team Management",
    customers: "Customer Directory",
    plans: "Plans",
    billing: "Billing",
    runs: "Runs",
    marketing: "Marketing Board",
  };

  const commonProps = {
    section,
    setSection,
    query,
    setQuery,
    loadingData,
    pageError,
    saveMessage,
    lastDbEcho,
    visibleUsers,
    selectedUserId,
    setSelectedUserId,
    selected,
    plans,
    runs,
    securityEvents,
    dodoSubscriptionStates,
    dodoWebhookEvents,
    teamUsers,
    customerUsers,
    profiles,
    newPlanCode,
    setNewPlanCode,
    newPlanName,
    setNewPlanName,
    planCapabilityDrafts,
    setPlanCapabilityDrafts,
    loadAdminData,
    saveUser,
    createPlan,
    savePlanCapabilities,
    savePlanIsActive,
    savePlanBetaAccess,
    handleLogout,
    titleBySection,
  };

  return <RedesignLayout {...commonProps} />;
}

function RedesignLayout(props: any) {
  const {
    section,
    setSection,
    query,
    setQuery,
    loadingData,
    pageError,
    saveMessage,
    lastDbEcho,
    visibleUsers,
    selectedUserId,
    setSelectedUserId,
    selected,
    plans,
    runs,
    securityEvents,
    dodoSubscriptionStates,
    dodoWebhookEvents,
    teamUsers,
    customerUsers,
    profiles,
    newPlanCode,
    setNewPlanCode,
    newPlanName,
    setNewPlanName,
    planCapabilityDrafts,
    setPlanCapabilityDrafts,
    loadAdminData,
    saveUser,
    createPlan,
    savePlanCapabilities,
    savePlanIsActive,
    savePlanBetaAccess,
    handleLogout,
    titleBySection,
  } = props;

  return (
    <div className="v1-shell">
      <aside className="v1-sidebar">
        <div className="v1-brand">
          <img
            src="https://pineengine.com/wp-content/uploads/2026/05/Pine_Engine_AI_Logo-Clear.png"
            alt="Pine Engine AI"
            className="v1-brand-logo"
          />
        </div>
        <nav className="v1-nav">
          <button className={section === "dashboard" ? "v1-nav-btn active" : "v1-nav-btn"} onClick={() => setSection("dashboard")}><LayoutDashboard size={16} /><span>Dashboard</span></button>
          <button className={section === "team" ? "v1-nav-btn active" : "v1-nav-btn"} onClick={() => setSection("team")}><BadgeCheck size={16} /><span>Team</span></button>
          <button className={section === "customers" ? "v1-nav-btn active" : "v1-nav-btn"} onClick={() => setSection("customers")}><Users size={16} /><span>Customers</span></button>
          <button className={section === "plans" ? "v1-nav-btn active" : "v1-nav-btn"} onClick={() => setSection("plans")}><CreditCard size={16} /><span>Plans</span></button>
          <button className={section === "billing" ? "v1-nav-btn active" : "v1-nav-btn"} onClick={() => setSection("billing")}><CreditCard size={16} /><span>Billing</span></button>
          <button className={section === "runs" ? "v1-nav-btn active" : "v1-nav-btn"} onClick={() => setSection("runs")}><Activity size={16} /><span>Runs</span></button>
          <button className={section === "marketing" ? "v1-nav-btn active" : "v1-nav-btn"} onClick={() => setSection("marketing")}><Megaphone size={16} /><span>Marketing</span></button>
        </nav>
        <div className="v1-links">
          <a className="v1-link-btn" href="https://hpanel.hostinger.com/websites/pineengine.com" target="_blank" rel="noreferrer">
            <span className="v1-link-label"><Monitor size={14} /> Hosting</span><ExternalLink size={14} />
          </a>
          <a className="v1-link-btn" href="https://mail.hostinger.com/mailboxes/INBOX?p=1&c=all" target="_blank" rel="noreferrer">
            <span className="v1-link-label"><Mail size={14} /> Mail</span><ExternalLink size={14} />
          </a>
          <a className="v1-link-btn" href="https://pineengine.app.n8n.cloud/workflow/yIcg5KuPmDq1UmJzObdCH" target="_blank" rel="noreferrer">
            <span className="v1-link-label"><PlugZap size={14} /> Automation</span><ExternalLink size={14} />
          </a>
          <a className="v1-link-btn" href="https://supabase.com/dashboard/project/jagalrfuaqxferkhtaex" target="_blank" rel="noreferrer">
            <span className="v1-link-label"><Database size={14} /> Database</span><ExternalLink size={14} />
          </a>
          <a className="v1-link-btn" href="https://app.dodopayments.com/home" target="_blank" rel="noreferrer">
            <span className="v1-link-label"><CreditCard size={14} /> Payments</span><ExternalLink size={14} />
          </a>
        </div>
        <div className="v1-side-footer">
          <button className="v1-refresh" onClick={loadAdminData} disabled={loadingData}>
            <RefreshCw size={15} /> {loadingData ? "Refreshing..." : "Refresh"}
          </button>
          <button className="v1-signout" onClick={handleLogout}><LogOut size={15} /> Sign out</button>
        </div>
      </aside>

      <section className="v1-main">
        <MainSection
          section={section}
          createPlan={createPlan}
          newPlanCode={newPlanCode}
          setNewPlanCode={setNewPlanCode}
          newPlanName={newPlanName}
          setNewPlanName={setNewPlanName}
          plans={plans}
          planCapabilityDrafts={planCapabilityDrafts}
          setPlanCapabilityDrafts={setPlanCapabilityDrafts}
          savePlanCapabilities={savePlanCapabilities}
          savePlanIsActive={savePlanIsActive}
          savePlanBetaAccess={savePlanBetaAccess}
          saveMessage={saveMessage}
          runs={runs}
    securityEvents={securityEvents}
    dodoSubscriptionStates={dodoSubscriptionStates}
    dodoWebhookEvents={dodoWebhookEvents}
          teamUsers={teamUsers}
          customerUsers={customerUsers}
          visibleUsers={visibleUsers}
          profiles={profiles}
          selectedUserId={selectedUserId}
          setSelectedUserId={setSelectedUserId}
          pageError={pageError}
          loadingData={loadingData}
          selected={selected}
          saveUser={saveUser}
          lastDbEcho={lastDbEcho}
          compact={true}
        />
      </section>
    </div>
  );
}

function MainSection(props: any) {
  const {
    section,
    createPlan,
    newPlanCode,
    setNewPlanCode,
    newPlanName,
    setNewPlanName,
    plans,
    planCapabilityDrafts,
    setPlanCapabilityDrafts,
    savePlanCapabilities,
    savePlanIsActive,
    savePlanBetaAccess,
    saveMessage,
    runs,
    securityEvents,
    dodoSubscriptionStates,
    dodoWebhookEvents,
    teamUsers,
    customerUsers,
    visibleUsers,
    profiles,
    selectedUserId,
    setSelectedUserId,
    pageError,
    loadingData,
    selected,
    saveUser,
    lastDbEcho,
    compact,
  } = props;

  const safeCustomerUsers: AppUser[] = Array.isArray(customerUsers) ? customerUsers : [];
  const safeRuns: SubmissionRun[] = Array.isArray(runs) ? runs : [];
  const safeSecurityEvents: SecurityEvent[] = Array.isArray(securityEvents) ? securityEvents : [];
  const safeDodoSubscriptionStates: DodoSubscriptionState[] = Array.isArray(dodoSubscriptionStates) ? dodoSubscriptionStates : [];
  const safeDodoWebhookEvents: DodoWebhookEvent[] = Array.isArray(dodoWebhookEvents) ? dodoWebhookEvents : [];
  const safeTeamUsers: TeamUser[] = Array.isArray(teamUsers) ? teamUsers : [];

  const totalCustomers = safeCustomerUsers.length;
  const freeUsers = safeCustomerUsers.filter((u: AppUser) => (u.plan_slug ?? "").toLowerCase() === "free").length;
  const betaUsers = safeCustomerUsers.filter((u: AppUser) => (u.plan_slug ?? "").toLowerCase().includes("beta")).length;
  const today = new Date();
  const isSameLocalDay = (iso: string) => {
    const d = new Date(iso);
    return (
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate()
    );
  };
  const newToday = safeCustomerUsers.filter((u: AppUser) => isSameLocalDay(u.created_at)).length;
  const loggedInToday = new Set(
    safeSecurityEvents
      .filter((evt) => evt.event_type === "login_success" && isSameLocalDay(evt.created_at))
      .map((evt) => (evt.user_id ?? evt.email ?? "").toLowerCase())
      .filter((key) => key.length > 0)
  ).size;
  const runsToday = safeRuns.filter((run) => isSameLocalDay(run.created_at)).length;
  const activityItems: ActivityItem[] = [
    ...safeRuns.map((run) => {
      const customer = safeCustomerUsers.find((u) => u.user_id === (run.pine_user_id ?? ""));
      const customerName = customer ? getDisplayName(customer) : "Unknown user";
      const rawStatus = (run.status ?? "").toLowerCase();
      return {
        time: run.created_at,
        event: rawStatus === "success" ? "Run Completed" : rawStatus === "error" || rawStatus === "failed" ? "Run Failed" : "Run Updated",
        actor: customerName,
        detail: `submission ${run.submission_id}`,
        actorUserId: customer?.user_id ?? null,
        targetUserId: customer?.user_id ?? null,
      };
    }),
    ...safeCustomerUsers.map((u) => ({
      time: u.created_at,
      event: "New Customer",
      actor: getDisplayName(u),
      detail: u.email,
      actorUserId: u.user_id,
      targetUserId: u.user_id,
    })),
    ...safeTeamUsers.map((u) => ({
      time: u.created_at,
      event: "Team Member Added",
      actor: getDisplayName(u),
      detail: `${u.email} (${u.role})`,
      actorUserId: u.user_id,
      targetUserId: u.user_id,
    })),
    ...safeCustomerUsers
      .filter((u) => (u.updated_at ?? "") > u.created_at)
      .map((u) => ({
        time: u.updated_at as string,
        event: "Customer Updated",
        actor: getDisplayName(u),
        detail: u.email,
        actorUserId: u.user_id,
        targetUserId: u.user_id,
      })),
    ...safeTeamUsers
      .filter((u) => (u.updated_at ?? "") > u.created_at)
      .map((u) => ({
        time: u.updated_at as string,
        event: "Team Updated",
        actor: getDisplayName(u),
        detail: `${u.email} (${u.role})`,
        actorUserId: u.user_id,
        targetUserId: u.user_id,
      })),
    ...safeSecurityEvents.map((evt) => {
      const actorTeam = safeTeamUsers.find((u) => u.user_id === (evt.user_id ?? ""));
      const actorCustomer = safeCustomerUsers.find((u) => u.user_id === (evt.user_id ?? ""));
      const actorName = actorTeam
        ? getDisplayName(actorTeam, profiles[actorTeam.user_id])
        : actorCustomer
          ? getDisplayName(actorCustomer)
          : evt.email ?? "Unknown user";
      const label =
        evt.event_type === "login_success"
          ? "Login"
          : evt.event_type === "logout"
            ? "Logout"
            : evt.event_type === "login_failed"
              ? "Login Failed"
              : "Security Event";
      const failureCode =
        evt.event_type === "login_failed" && evt.metadata && typeof evt.metadata.failure_code === "string"
          ? ` (${evt.metadata.failure_code})`
          : "";
      return {
        time: evt.created_at,
        event: label,
        actor: actorName,
        detail: `${evt.email ?? evt.event_type}${failureCode}`,
        actorUserId: evt.user_id,
        targetUserId: evt.user_id,
      };
    }),
    ...safeDodoWebhookEvents.map((evt) => {
      const mappedCustomer = safeCustomerUsers.find((u) => u.user_id === (evt.user_id ?? ""));
      const actorName = mappedCustomer ? getDisplayName(mappedCustomer) : "Billing System";
      return {
        time: evt.created_at,
        event: "Billing Event",
        actor: actorName,
        detail: `${evt.event_type ?? "unknown"} • ${evt.event_id}`,
        actorUserId: mappedCustomer?.user_id ?? evt.user_id ?? null,
        targetUserId: mappedCustomer?.user_id ?? evt.user_id ?? null,
      };
    }),
  ]
    .filter((item) => !!item.time)
    .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
    .slice(0, 500);
  const selectedUserActivity =
    selected
      ? activityItems.filter((item) => item.actorUserId === selected.user.user_id || item.targetUserId === selected.user.user_id).slice(0, 100)
      : [];

  const safeVisibleUsers: Array<TeamUser | AppUser> = Array.isArray(visibleUsers) ? visibleUsers : [];
  const teamRows = safeVisibleUsers as TeamUser[];
  const customerRows = safeVisibleUsers as AppUser[];
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [activityPeriod, setActivityPeriod] = useState<"24h" | "7d" | "30d">("7d");
  const [activityPage, setActivityPage] = useState(1);
  const [runsPeriod, setRunsPeriod] = useState<"24h" | "7d" | "30d">("7d");
  const [runsPage, setRunsPage] = useState(1);
  const [customersSearch, setCustomersSearch] = useState("");
  const [runsSearch, setRunsSearch] = useState("");
  const [billingPeriod, setBillingPeriod] = useState<"24h" | "7d" | "30d">("7d");
  const [billingSearch, setBillingSearch] = useState("");
  const [billingPage, setBillingPage] = useState(1);
  const periodCutoffMs =
    activityPeriod === "24h"
      ? 24 * 60 * 60 * 1000
      : activityPeriod === "7d"
        ? 7 * 24 * 60 * 60 * 1000
        : 30 * 24 * 60 * 60 * 1000;
  const cutoff = Date.now() - periodCutoffMs;
  const filteredActivity = activityItems.filter((item) => new Date(item.time).getTime() >= cutoff);
  const pageSize = 25;
  const totalPages = Math.max(1, Math.ceil(filteredActivity.length / pageSize));
  const currentPage = Math.min(activityPage, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pagedActivity = filteredActivity.slice(pageStart, pageStart + pageSize);
  const runsCutoffMs =
    runsPeriod === "24h"
      ? 24 * 60 * 60 * 1000
      : runsPeriod === "7d"
        ? 7 * 24 * 60 * 60 * 1000
        : 30 * 24 * 60 * 60 * 1000;
  const runsCutoff = Date.now() - runsCutoffMs;
  const filteredCustomerRows =
    section === "customers" && customersSearch.trim() !== ""
      ? customerRows.filter((user) => {
          const q = customersSearch.trim().toLowerCase();
          return (
            getDisplayName(user).toLowerCase().includes(q) ||
            (user.email ?? "").toLowerCase().includes(q) ||
            String(user.user_id ?? "").toLowerCase().includes(q)
          );
        })
      : customerRows;
  const periodFilteredRuns = safeRuns.filter((run) => new Date(run.created_at).getTime() >= runsCutoff);
  const filteredRuns =
    runsSearch.trim() === ""
      ? periodFilteredRuns
      : periodFilteredRuns.filter((run) => {
          const q = runsSearch.trim().toLowerCase();
          const customer = safeCustomerUsers.find((u: AppUser) => u.user_id === (run.pine_user_id ?? ""));
          const customerName = customer ? getDisplayName(customer) : "Unknown";
          const rawStatus = (run.status ?? "").toUpperCase();
          const statusLabel = rawStatus === "SUCCESS" ? "done" : rawStatus || "unknown";
          return (
            String(run.submission_id ?? "").toLowerCase().includes(q) ||
            String(run.pine_user_id ?? "").toLowerCase().includes(q) ||
            String(customerName).toLowerCase().includes(q) ||
            String(run.error_class ?? "").toLowerCase().includes(q) ||
            String(statusLabel).toLowerCase().includes(q)
          );
        });
  const runsPageSize = 25;
  const runsTotalPages = Math.max(1, Math.ceil(filteredRuns.length / runsPageSize));
  const runsCurrentPage = Math.min(runsPage, runsTotalPages);
  const runsPageStart = (runsCurrentPage - 1) * runsPageSize;
  const pagedRuns = filteredRuns.slice(runsPageStart, runsPageStart + runsPageSize);

  const billingActiveSubs = safeDodoSubscriptionStates.filter((s) => !!s.dodo_subscription_id).length;
  const billingFailedAccounts = safeDodoSubscriptionStates.filter((s) => (s.failed_attempts ?? 0) > 0).length;
  const billingEvents7d = safeDodoWebhookEvents.filter((evt) => new Date(evt.created_at).getTime() >= Date.now() - 7 * 24 * 60 * 60 * 1000).length;
  const lastBillingEventAt = safeDodoWebhookEvents[0]?.created_at ?? null;
  const billingCutoffMs =
    billingPeriod === "24h"
      ? 24 * 60 * 60 * 1000
      : billingPeriod === "7d"
        ? 7 * 24 * 60 * 60 * 1000
        : 30 * 24 * 60 * 60 * 1000;
  const billingCutoff = Date.now() - billingCutoffMs;
  const billingTimeline = [
    ...safeDodoSubscriptionStates.map((state) => {
      const customer = safeCustomerUsers.find((u) => u.user_id === state.user_id);
      return {
        time: state.updated_at,
        rowType: "Subscription State",
        user: customer ? getDisplayName(customer) : state.user_id,
        email: customer?.email ?? "N/A",
        plan: customer?.plan_slug ?? "N/A",
        eventType: "state_update",
        eventId: state.dodo_subscription_id ?? state.dodo_customer_id ?? "N/A",
        failedAttempts: Number(state.failed_attempts ?? 0),
        details: `cust=${state.dodo_customer_id ?? "N/A"} | sub=${state.dodo_subscription_id ?? "N/A"}`,
      };
    }),
    ...safeDodoWebhookEvents.map((evt) => {
      const customer = safeCustomerUsers.find((u) => u.user_id === (evt.user_id ?? ""));
      return {
        time: evt.created_at,
        rowType: "Webhook Event",
        user: customer ? getDisplayName(customer) : (evt.user_id ?? "N/A"),
        email: customer?.email ?? "N/A",
        plan: customer?.plan_slug ?? "N/A",
        eventType: evt.event_type ?? "unknown",
        eventId: evt.event_id,
        failedAttempts: null as number | null,
        details: `webhook_id=${evt.id}`,
      };
    }),
  ];
  const billingFilteredByPeriod = billingTimeline.filter((row) => new Date(row.time).getTime() >= billingCutoff);
  const filteredBillingRows =
    billingSearch.trim() === ""
      ? billingFilteredByPeriod
      : billingFilteredByPeriod.filter((row) => {
          const q = billingSearch.trim().toLowerCase();
          return (
            String(row.rowType).toLowerCase().includes(q) ||
            String(row.user).toLowerCase().includes(q) ||
            String(row.email).toLowerCase().includes(q) ||
            String(row.plan).toLowerCase().includes(q) ||
            String(row.eventType).toLowerCase().includes(q) ||
            String(row.eventId).toLowerCase().includes(q) ||
            String(row.details).toLowerCase().includes(q)
          );
        });
  const billingPageSize = 25;
  const billingTotalPages = Math.max(1, Math.ceil(filteredBillingRows.length / billingPageSize));
  const billingCurrentPage = Math.min(billingPage, billingTotalPages);
  const billingPageStart = (billingCurrentPage - 1) * billingPageSize;
  const pagedBillingRows = filteredBillingRows.slice(billingPageStart, billingPageStart + billingPageSize);

  if (section === "dashboard") {
    return (
      <section>
        <KpiAndActions
          cards={[
            { label: "Total Customers", value: String(totalCustomers), icon: <Users size={16} /> },
            { label: "Free Users", value: String(freeUsers), icon: <UserRound size={16} /> },
            { label: "Beta Users", value: String(betaUsers), icon: <BadgeCheck size={16} /> },
            { label: "New Today", value: String(newToday), icon: <UserPlus size={16} /> },
            { label: "Logged In Today", value: String(loggedInToday), icon: <BadgeCheck size={16} /> },
            { label: "Runs Today", value: String(runsToday), icon: <Activity size={16} /> },
          ]}
          actions={[]}
        />
        <div className="panel">
          <div className="dashboard-head">
            <h2>Recent Activity</h2>
            <label className="dashboard-period">
              Period
              <select
                value={activityPeriod}
                onChange={(e) => {
                  setActivityPeriod(e.target.value as "24h" | "7d" | "30d");
                  setActivityPage(1);
                }}
              >
                <option value="24h">Last 24h</option>
                <option value="7d">Last 7d</option>
                <option value="30d">Last 30d</option>
              </select>
            </label>
          </div>
          {loadingData && <p>Loading activity...</p>}
          {pageError && <p className="error">{pageError}</p>}
          {!loadingData && !pageError && safeRuns.length === 0 && (
            <p className="hint">No recent submissions available yet.</p>
          )}
          <div className="runs-table-wrap">
            <table className="runs-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Event</th>
                  <th>User</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {pagedActivity.map((item, index) => (
                  <tr key={`${item.event}-${item.time}-${index}`}>
                    <td>{formatLocalDateTime(item.time)}</td>
                    <td>
                      <span className={`event-pill event-${item.event.toLowerCase().replace(/\s+/g, "-")}`}>
                        {item.event}
                      </span>
                    </td>
                    <td>{item.actor}</td>
                    <td>{item.detail}</td>
                  </tr>
                ))}
                {!loadingData && pagedActivity.length === 0 && (
                  <tr>
                    <td colSpan={4}>No activity records available.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="v1-table-footer">
            <span>
              Showing {filteredActivity.length === 0 ? 0 : pageStart + 1} to{" "}
              {Math.min(pageStart + pageSize, filteredActivity.length)} of {filteredActivity.length} events
            </span>
            <div className="v1-pager">
              <button type="button" onClick={() => setActivityPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1}>
                Prev
              </button>
              <span>
                {currentPage} / {totalPages}
              </span>
              <button type="button" onClick={() => setActivityPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>
                Next
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (section === "plans") {
    return (
      <section>
        <div className="panel">
          <h2>Plans</h2>
        <form className="plan-form" onSubmit={createPlan}>
          <label>
            Plan code
            <input value={newPlanCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPlanCode(e.target.value)} placeholder="starter" required />
          </label>
          <label>
            Plan name
            <input value={newPlanName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPlanName(e.target.value)} placeholder="Starter" required />
          </label>
          <button type="submit">Create plan</button>
        </form>
        <div className="plans-list">
          <h3 className="plans-list-title">Plans</h3>
          <div className="v1-table-wrap">
            <table className="v1-table plans-table">
              <thead>
                <tr>
                  <th>Plan Name</th>
                  <th>Plan Code</th>
                  <th>Status</th>
                  <th>Access</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((p: Plan) => (
                  <tr key={p.id}>
                    <td><strong>{p.name}</strong></td>
                    <td>{p.code}</td>
                    <td>
                      <span className={`pill ${p.is_active ? "active" : "inactive"}`}>
                        {p.is_active ? "active" : "inactive"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`pill ${
                          Boolean((p.capabilities as Record<string, unknown> | null)?.access_enabled)
                            ? "active"
                            : "inactive"
                        }`}
                      >
                        {Boolean((p.capabilities as Record<string, unknown> | null)?.access_enabled)
                          ? "enabled"
                          : "disabled"}
                      </span>
                    </td>
                    <td>
                      <div className="plan-actions">
                        <button
                          type="button"
                          onClick={() => void savePlanIsActive(p.id, !p.is_active)}
                        >
                          {p.is_active ? "Set Inactive" : "Set Active"}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            void savePlanBetaAccess(
                              p.id,
                              !Boolean((p.capabilities as Record<string, unknown> | null)?.access_enabled),
                            )
                          }
                        >
                          {Boolean((p.capabilities as Record<string, unknown> | null)?.access_enabled)
                            ? "Disable Access"
                            : "Enable Access"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {saveMessage && <p>{saveMessage}</p>}
        </div>
      </section>
    );
  }

  if (section === "billing") {
    return (
      <section>
        <KpiAndActions
          cards={[
            { label: "Accounts with Subscription", value: String(billingActiveSubs), icon: <CreditCard size={16} /> },
            { label: "Accounts with Failed Attempts", value: String(billingFailedAccounts), icon: <BadgeCheck size={16} /> },
            { label: "Webhook Events (7d)", value: String(billingEvents7d), icon: <Activity size={16} /> },
            { label: "Last Webhook Received", value: lastBillingEventAt ? formatLocalDateTime(lastBillingEventAt) : "N/A", icon: <RefreshCw size={16} /> },
          ]}
          actions={["Dodo + Supabase Billing Overview"]}
        />

        <div className="panel" style={{ marginTop: "0.75rem" }}>
          <div className="dashboard-head">
            <h2>Billing Feed</h2>
            <input
              className="v1-filter-input"
              placeholder="Search user, email, event, IDs"
              value={billingSearch}
              onChange={(e) => {
                setBillingSearch(e.target.value);
                setBillingPage(1);
              }}
            />
            <label className="dashboard-period">
              Period
              <select
                value={billingPeriod}
                onChange={(e) => {
                  setBillingPeriod(e.target.value as "24h" | "7d" | "30d");
                  setBillingPage(1);
                }}
              >
                <option value="24h">Last 24h</option>
                <option value="7d">Last 7d</option>
                <option value="30d">Last 30d</option>
              </select>
            </label>
          </div>
          {loadingData && <p>Loading billing feed...</p>}
          {pageError && <p className="error">{pageError}</p>}
          {!loadingData && !pageError && filteredBillingRows.length === 0 && (
            <p className="hint">No billing records found for this period/search.</p>
          )}
          <div className="runs-table-wrap">
            <table className="v1-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Record Type</th>
                  <th>User</th>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Event Type</th>
                  <th>Event / Subscription ID</th>
                  <th>Failed Attempts</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {pagedBillingRows.map((row, idx) => {
                  return (
                    <tr key={`${row.rowType}-${row.eventId}-${row.time}-${idx}`}>
                      <td>{formatLocalDateTime(row.time)}</td>
                      <td>{row.rowType}</td>
                      <td>{row.user}</td>
                      <td>{row.email}</td>
                      <td>{row.plan}</td>
                      <td>{row.eventType}</td>
                      <td>{row.eventId}</td>
                      <td>{row.failedAttempts === null ? "-" : String(row.failedAttempts)}</td>
                      <td>{row.details}</td>
                    </tr>
                  );
                })}
                {!loadingData && pagedBillingRows.length === 0 && (
                  <tr>
                    <td colSpan={9}>No billing rows found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="v1-table-footer">
            <span>
              Showing {filteredBillingRows.length === 0 ? 0 : billingPageStart + 1} to{" "}
              {Math.min(billingPageStart + billingPageSize, filteredBillingRows.length)} of {filteredBillingRows.length} billing records
            </span>
            <div className="v1-pager">
              <button type="button" onClick={() => setBillingPage((p) => Math.max(1, p - 1))} disabled={billingCurrentPage <= 1}>
                Prev
              </button>
              <span>
                {billingCurrentPage} / {billingTotalPages}
              </span>
              <button type="button" onClick={() => setBillingPage((p) => Math.min(billingTotalPages, p + 1))} disabled={billingCurrentPage >= billingTotalPages}>
                Next
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (section === "marketing") {
    return (
      <section>
        <div className="panel">
          <h2>Marketing Board</h2>
          <p className="hint">Placeholder for n8n-powered content operations.</p>
          <div className="v1-kpi-grid">
            <div className="v1-kpi-card">
              <span>Blog Post Of Week</span>
              <strong>Coming Soon</strong>
            </div>
            <div className="v1-kpi-card">
              <span>Script Of Week</span>
              <strong>Coming Soon</strong>
            </div>
            <div className="v1-kpi-card">
              <span>Queue Status</span>
              <strong>Coming Soon</strong>
            </div>
            <div className="v1-kpi-card">
              <span>n8n Suggestions</span>
              <strong>Coming Soon</strong>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (section === "runs") {
    return (
      <section>
        <div className="panel">
          <div className="dashboard-head">
            <h2>Runs</h2>
            <input
              className="v1-filter-input"
              placeholder="Search run ID, customer, status"
              value={runsSearch}
              onChange={(e) => {
                setRunsSearch(e.target.value);
                setRunsPage(1);
              }}
            />
            <label className="dashboard-period">
              Period
              <select
                value={runsPeriod}
                onChange={(e) => {
                  setRunsPeriod(e.target.value as "24h" | "7d" | "30d");
                  setRunsPage(1);
                }}
              >
                <option value="24h">Last 24h</option>
                <option value="7d">Last 7d</option>
                <option value="30d">Last 30d</option>
              </select>
            </label>
          </div>
          {loadingData && <p>Loading runs...</p>}
          {pageError && <p className="error">{pageError}</p>}
          {!loadingData && !pageError && filteredRuns.length === 0 && (
            <p className="hint">No rows returned from <code>public.submissions</code> for this admin session.</p>
          )}
        <div className="runs-table-wrap">
          <table className="runs-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Customer</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Error</th>
                <th>Run ID</th>
              </tr>
            </thead>
            <tbody>
              {pagedRuns.map((run: SubmissionRun) => {
                const customer = safeCustomerUsers.find((u: AppUser) => u.user_id === (run.pine_user_id ?? ""));
                const customerName = customer ? getDisplayName(customer) : "Unknown";
                const planValue = customer?.plan_slug || run.pine_plan_id || "N/A";
                const rawStatus = (run.status ?? "").toUpperCase();
                const statusLabel = rawStatus === "SUCCESS" ? "Done" : rawStatus || "Unknown";
                const statusClass =
                  rawStatus === "SUCCESS"
                    ? "ok"
                    : rawStatus === "ERROR" || rawStatus === "FAILED"
                      ? "bad"
                      : rawStatus === "PROCESSING" || rawStatus === "RECEIVED"
                        ? "warn"
                        : "neutral";

                return (
                  <tr key={run.submission_id}>
                    <td>{formatLocalDateTime(run.created_at)}</td>
                    <td>{customerName}</td>
                    <td>{planValue}</td>
                    <td><span className={`run-status ${statusClass}`}>{statusLabel}</span></td>
                    <td>{run.error_class ?? "-"}</td>
                    <td>{run.submission_id}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          <div className="v1-table-footer">
            <span>
              Showing {filteredRuns.length === 0 ? 0 : runsPageStart + 1} to{" "}
              {Math.min(runsPageStart + runsPageSize, filteredRuns.length)} of {filteredRuns.length} runs
            </span>
            <div className="v1-pager">
              <button type="button" onClick={() => setRunsPage((p) => Math.max(1, p - 1))} disabled={runsCurrentPage <= 1}>
                Prev
              </button>
              <span>
                {runsCurrentPage} / {runsTotalPages}
              </span>
              <button type="button" onClick={() => setRunsPage((p) => Math.min(runsTotalPages, p + 1))} disabled={runsCurrentPage >= runsTotalPages}>
                Next
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <main>
      <div
        className={
          compact && (section === "customers" || section === "team")
            ? "content-grid content-grid--single"
            : compact
              ? "content-grid content-grid--v1"
              : "content-grid"
        }
      >
      <section className="panel list-panel">
        <div className="v1-list-header">
          <div>
            <h2>{section === "team" ? "Team" : "Customers"}</h2>
            <p>{section === "team" ? "Manage internal members and access roles." : "Manage your user base and subscription plans."}</p>
          </div>
          {compact && (
            <div className="v1-filter-row">
              {section === "customers" && (
                <input
                  className="v1-filter-input"
                  placeholder="Search name, email, user ID"
                  value={customersSearch}
                  onChange={(e) => setCustomersSearch(e.target.value)}
                />
              )}
              <select defaultValue="">
                <option value="">Status: All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              {section === "customers" && (
                <select defaultValue="">
                  <option value="">Plan: All</option>
                  <option value="free">Free</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              )}
            </div>
          )}
        </div>
        {loadingData && <p>Loading users...</p>}
        {pageError && <p className="error">{pageError}</p>}
        {compact ? (
          section === "customers" ? (
            <div className="v1-table-wrap">
              <table className="v1-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Auth</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>Joined Date</th>
                    <th>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomerRows.map((user) => {
                    const isSelected = selectedUserId === user.id;
                    return (
                      <tr
                        key={user.id}
                        className={isSelected ? "active-row" : ""}
                        onClick={() => {
                          setSelectedUserId(user.id);
                          setCustomerModalOpen(true);
                        }}
                      >
                        <td>{getDisplayName(user)}</td>
                        <td>{user.email}</td>
                        <td>{formatAuthProvider(user.auth_provider)}</td>
                        <td>{user.plan_slug || "No plan"}</td>
                        <td>
                          <span className={`pill ${user.is_active ? "active" : "inactive"}`}>
                            {user.is_active ? "active" : "inactive"}
                          </span>
                        </td>
                        <td>{formatLocalDateTime(user.created_at)}</td>
                        <td>{formatLocalDateTime(user.updated_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="v1-table-footer">
                <span>Showing {filteredCustomerRows.length} customer{filteredCustomerRows.length === 1 ? "" : "s"}</span>
              </div>
            </div>
          ) : (
            <div className="v1-table-wrap">
              <table className="v1-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last Update</th>
                  </tr>
                </thead>
                <tbody>
                  {teamRows.map((user) => {
                    const profile = profiles[user.user_id];
                    const access = profile?.access_status ?? (user.is_active ? "active" : "inactive");
                    const isSelected = selectedUserId === user.id;
                    return (
                      <tr
                        key={user.id}
                        className={isSelected ? "active-row" : ""}
                        onClick={() => {
                          setSelectedUserId(user.id);
                          setTeamModalOpen(true);
                        }}
                      >
                        <td>{getDisplayName(user, profile)}</td>
                        <td>{user.email}</td>
                        <td>{user.role}</td>
                        <td>
                          <span className={`pill ${access === "active" ? "active" : "inactive"}`}>{access}</span>
                        </td>
                        <td>{formatLocalDateTime(user.updated_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="v1-table-footer">
                <span>Showing {teamRows.length} team member{teamRows.length === 1 ? "" : "s"}</span>
              </div>
            </div>
          )
        ) : (
          <div className="user-list">
            {visibleUsers.map((user: TeamUser | AppUser) => {
              const profile = profiles[user.user_id];
              const isSelected = selectedUserId === user.id;
              const displayName = section === "customers" ? getDisplayName(user) : getDisplayName(user, profile);
              const access =
                section === "customers"
                  ? (user.is_active ? "active" : "inactive")
                  : profile?.access_status ?? (user.is_active ? "active" : "inactive");
              const authDetail = section === "customers"
                ? ` · ${formatAuthProvider((user as AppUser).auth_provider)}`
                : "";

              return (
                <button key={user.id} className={`user-row ${isSelected ? "active" : ""}`} onClick={() => setSelectedUserId(user.id)}>
                  <div className="user-row-top">
                    <strong>{displayName}</strong>
                    <span className={`pill ${access === "active" ? "active" : "inactive"}`}>{access}</span>
                  </div>
                  <div className="user-row-meta">{user.email}{authDetail}</div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {!(compact && (section === "customers" || section === "team")) && (
        <section className="panel detail-panel">
          <h2>{section === "team" ? "Team Member Profile" : "Customer Profile"}</h2>
          {selected ? (
            <UserForm
              key={`${selected.kind}-${selected.user.id}`}
              selected={selected}
              plans={plans}
              userActivity={selectedUserActivity}
              onSave={saveUser}
              saveMessage={saveMessage}
              lastDbEcho={lastDbEcho}
            />
          ) : (
            <p>Select a user.</p>
          )}
        </section>
      )}
      </div>
      {compact && section === "customers" && customerModalOpen && selected && (
        <div className="v1-modal-backdrop" onClick={() => setCustomerModalOpen(false)}>
          <section className="v1-modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="v1-modal-head">
              <h2>Customer Profile</h2>
              <button type="button" className="v1-modal-close" onClick={() => setCustomerModalOpen(false)}>Close</button>
            </div>
            <div className="v1-tabs">
              <span>Profile</span>
            </div>
            <UserForm
              key={`modal-${selected.kind}-${selected.user.id}`}
              selected={selected}
              plans={plans}
              userActivity={selectedUserActivity}
              onSave={saveUser}
              saveMessage={saveMessage}
              lastDbEcho={lastDbEcho}
            />
          </section>
        </div>
      )}
      {compact && section === "team" && teamModalOpen && selected && (
        <div className="v1-modal-backdrop" onClick={() => setTeamModalOpen(false)}>
          <section className="v1-modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="v1-modal-head">
              <h2>Team Member Profile</h2>
              <button type="button" className="v1-modal-close" onClick={() => setTeamModalOpen(false)}>Close</button>
            </div>
            <div className="v1-tabs">
              <span>Profile</span>
            </div>
            <UserForm
              key={`modal-team-${selected.kind}-${selected.user.id}`}
              selected={selected}
              plans={plans}
              userActivity={selectedUserActivity}
              onSave={saveUser}
              saveMessage={saveMessage}
              lastDbEcho={lastDbEcho}
            />
          </section>
        </div>
      )}
    </main>
  );
}

function KpiAndActions({
  cards,
  actions,
}: {
  cards: { label: string; value: string; icon?: React.ReactNode }[];
  actions: string[];
}) {
  return (
    <>
      <section className="v1-kpi-grid">
        {cards.map((card) => (
          <div key={card.label} className="v1-kpi-card">
            <span className="v1-kpi-label">
              {card.label}
              {card.icon ? <i className="v1-kpi-icon">{card.icon}</i> : null}
            </span>
            <strong>{card.value}</strong>
          </div>
        ))}
      </section>
      {actions.length > 0 && (
        <section className="v1-capsules">
          {actions.map((action) => (
            <span key={action} className="v1-capsule">{action}</span>
          ))}
        </section>
      )}
    </>
  );
}

function UserForm({
  selected,
  plans,
  userActivity,
  onSave,
  saveMessage,
  lastDbEcho,
}: {
  selected: SelectedUser;
  plans: Plan[];
  userActivity: ActivityItem[];
  onSave: (form: {
    firstName: string;
    lastName: string;
    email: string;
    accessStatus: "active" | "inactive";
    role?: TeamRole;
    planSlug: string;
  }) => Promise<void>;
  saveMessage: string | null;
  lastDbEcho: string | null;
}) {
  const [profileTab, setProfileTab] = useState<"overview" | "billing" | "activity">("overview");
  const [firstName, setFirstName] = useState(selected.user.first_name ?? "");
  const [lastName, setLastName] = useState(selected.user.last_name ?? "");
  const [email, setEmail] = useState(selected.user.email ?? "");
  const [role, setRole] = useState<TeamRole>(selected.kind === "team" ? selected.user.role : "team");
  const [accessStatus, setAccessStatus] = useState<"active" | "inactive">(
    selected.kind === "customer"
      ? (selected.user.is_active ? "active" : "inactive")
      : selected.profile?.access_status ?? (selected.user.is_active ? "active" : "inactive"),
  );
  const [planSlug, setPlanSlug] = useState(selected.kind === "customer" ? selected.user.plan_slug ?? "" : "");
  const [saving, setSaving] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [billingState, setBillingState] = useState<DodoSubscriptionState | null>(null);
  const [billingEvents, setBillingEvents] = useState<DodoWebhookEvent[]>([]);

  useEffect(() => {
    if (selected.kind !== "customer") {
      setBillingLoading(false);
      setBillingError(null);
      setBillingState(null);
      setBillingEvents([]);
      if (profileTab === "billing") setProfileTab("overview");
      return;
    }

    let cancelled = false;

    const loadBilling = async () => {
      setBillingLoading(true);
      setBillingError(null);

      const [{ data: stateData, error: stateError }, { data: eventsData, error: eventsError }] = await Promise.all([
        supabase
          .from("dodo_subscription_state")
          .select("user_id,dodo_customer_id,dodo_subscription_id,failed_attempts,last_event_id,updated_at")
          .eq("user_id", selected.user.user_id)
          .maybeSingle(),
        supabase
          .from("dodo_webhook_events")
          .select("event_id,event_type,created_at")
          .eq("user_id", selected.user.user_id)
          .order("created_at", { ascending: false })
          .limit(20),
      ]);

      if (cancelled) return;

      if (stateError || eventsError) {
        setBillingError((stateError ?? eventsError)?.message ?? "Unable to load billing data.");
        setBillingState(null);
        setBillingEvents([]);
        setBillingLoading(false);
        return;
      }

      setBillingState((stateData as DodoSubscriptionState | null) ?? null);
      setBillingEvents((eventsData as DodoWebhookEvent[] | null) ?? []);
      setBillingLoading(false);
    };

    void loadBilling();
    return () => {
      cancelled = true;
    };
  }, [selected.kind, selected.user.user_id, profileTab]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    await onSave({
      firstName,
      lastName,
      email,
      accessStatus,
      role: selected.kind === "team" ? role : undefined,
      planSlug,
    });
    setSaving(false);
  };

  return (
    <>
      <div className="v1-tabs">
        <button type="button" className={profileTab === "overview" ? "active" : ""} onClick={() => setProfileTab("overview")}>
          Overview
        </button>
        {selected.kind === "customer" && (
          <button type="button" className={profileTab === "billing" ? "active" : ""} onClick={() => setProfileTab("billing")}>
            Billing
          </button>
        )}
        <button type="button" className={profileTab === "activity" ? "active" : ""} onClick={() => setProfileTab("activity")}>
          Activity
        </button>
      </div>
      {profileTab === "overview" ? (
        <form className="user-form user-form--two-col" onSubmit={submit}>
          <label>
            User name (first)
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
          </label>
          <label>
            User name (last)
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} required />
          </label>
          <label className="span-2">
            User email
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label>
            User joined at
            <input value={formatLocalDateTime(selected.user.created_at)} disabled />
          </label>
          <label>
            Last DB update
            <input value={formatLocalDateTime(selected.user.updated_at)} disabled />
          </label>
          <label>
            User ID
            <input value={selected.user.user_id} disabled />
          </label>
          {selected.kind === "team" && (
            <label>
              User role
              <select value={role} onChange={(e) => setRole(e.target.value as TeamRole)}>
                <option value="admin">admin</option>
                <option value="manager">manager</option>
                <option value="team">team</option>
              </select>
            </label>
          )}
          <label>
            User access status
            <select value={accessStatus} onChange={(e) => setAccessStatus(e.target.value as "active" | "inactive")}>
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </label>
          {selected.kind === "customer" && (
            <label>
              Auth provider
              <input value={formatAuthProvider(selected.user.auth_provider)} disabled />
            </label>
          )}
          {selected.kind === "customer" && (
            <label>
              User plan
              <select value={planSlug} onChange={(e) => setPlanSlug(e.target.value)}>
                <option value="">No plan</option>
                {plans.filter((p) => p.is_active).map((plan) => (
                  <option key={plan.id} value={plan.code}>{plan.name} ({plan.code})</option>
                ))}
              </select>
            </label>
          )}

          <button className="span-2" type="submit" disabled={saving}>{saving ? "Saving..." : "Save user"}</button>
          {saveMessage && <p className="span-2">{saveMessage}</p>}
          {lastDbEcho && <p className="hint span-2">DB echo: {lastDbEcho}</p>}
          {selected.kind === "team" && role === "admin" && <p className="hint span-2">Admin users do not require a plan.</p>}
        </form>
      ) : profileTab === "billing" && selected.kind === "customer" ? (
        <div className="runs-table-wrap">
          {billingLoading && <p>Loading billing data...</p>}
          {!billingLoading && billingError && <p>{billingError}</p>}
          {!billingLoading && !billingError && (
            <>
              <table className="runs-table">
                <tbody>
                  <tr><th style={{ width: "220px" }}>Current plan</th><td>{selected.user.plan_slug ?? "N/A"}</td></tr>
                  <tr><th>Auth provider</th><td>{formatAuthProvider(selected.user.auth_provider)}</td></tr>
                  <tr><th>Plan status</th><td>{accessStatus}</td></tr>
                  <tr><th>Dodo customer ID</th><td>{billingState?.dodo_customer_id ?? "N/A"}</td></tr>
                  <tr><th>Dodo subscription ID</th><td>{billingState?.dodo_subscription_id ?? "N/A"}</td></tr>
                  <tr><th>Failed renewals</th><td>{String(billingState?.failed_attempts ?? 0)}</td></tr>
                  <tr><th>Last event ID</th><td>{billingState?.last_event_id ?? "N/A"}</td></tr>
                  <tr><th>State updated</th><td>{formatLocalDateTime(billingState?.updated_at)}</td></tr>
                </tbody>
              </table>

              <h3 style={{ marginTop: "14px" }}>Recent Webhook Events</h3>
              <table className="runs-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Event Type</th>
                    <th>Event ID</th>
                  </tr>
                </thead>
                <tbody>
                  {billingEvents.map((item) => (
                    <tr key={item.event_id}>
                      <td>{formatLocalDateTime(item.created_at)}</td>
                      <td>{item.event_type ?? "unknown"}</td>
                      <td>{item.event_id}</td>
                    </tr>
                  ))}
                  {billingEvents.length === 0 && (
                    <tr>
                      <td colSpan={3}>No billing events recorded for this user yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      ) : (
        <div className="runs-table-wrap">
          <table className="runs-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Event</th>
                <th>User</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {userActivity.map((item, index) => (
                <tr key={`${item.event}-${item.time}-${index}`}>
                  <td>{formatLocalDateTime(item.time)}</td>
                  <td>
                    <span className={`event-pill event-${item.event.toLowerCase().replace(/\s+/g, "-")}`}>
                      {item.event}
                    </span>
                  </td>
                  <td>{item.actor}</td>
                  <td>{item.detail}</td>
                </tr>
              ))}
              {userActivity.length === 0 && (
                <tr>
                  <td colSpan={4}>No activity records for this user yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default App;
